import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useCart } from '../../contexts/CartContext';
import { useCheckout } from './hooks/useCheckout';
import { usePaymentSettings } from '../../hooks/usePaymentSettings';
import AddressForm from './components/AddressForm';
import PaymentStep from './components/PaymentStep';
import OrderSummary from './components/OrderSummary';

const STEPS = [
  { key: 'address', label: 'Adres' },
  { key: 'payment', label: 'Ödeme' },
  { key: 'confirm', label: 'Tamamlandı' },
] as const;

export default function CheckoutPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const { settings, loading: settingsLoading } = usePaymentSettings();

  const checkout = useCheckout(
    items,
    totalPrice,
    settings.kapida_odeme_ucreti,
    settings.kargo_ucreti,
    settings.ucretsiz_kargo_limiti,
    settings.kapida_odeme_ust_limit
  );

  if (items.length === 0 && checkout.step !== 'confirm') {
    return (
      <div className="min-h-screen bg-cream-light font-sans">
        <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
        <main className="pt-32 pb-20 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-cream rounded-full mx-auto mb-4">
              <i className="ri-shopping-cart-line text-3xl text-coffee-light" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-coffee mb-2">Sepetiniz Boş</h2>
            <p className="text-coffee-light mb-6">Ödeme yapmak için önce sepete ürün ekleyin.</p>
            <button
              onClick={() => navigate('/products')}
              className="px-6 py-3 bg-coffee text-white rounded-xl hover:bg-coffee-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Ürünlere Gözat
            </button>
          </div>
        </main>
        <Footer />
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h1 className="font-serif text-3xl font-bold text-coffee mb-8">Sipariş Tamamla</h1>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-10">
            {STEPS.map((s, i) => {
              const currentIdx = STEPS.findIndex(x => x.key === checkout.step);
              const isDone = i < currentIdx;
              const isActive = s.key === checkout.step;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                      isActive ? 'bg-coffee text-white' : isDone ? 'bg-gold text-white' : 'bg-cream-dark text-coffee-light'
                    }`}
                  >
                    {isDone ? <i className="ri-check-line text-sm" /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium hidden sm:block ${isActive ? 'text-coffee' : 'text-coffee-light'}`}>
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && <div className="w-12 h-px bg-cream-dark mx-1" />}
                </div>
              );
            })}
          </div>

          {checkout.step === 'confirm' ? (
            /* Success Screen */
            <div className="max-w-lg mx-auto bg-white rounded-2xl p-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-coffee mb-2">Sipariş Alındı!</h2>
              <p className="text-coffee-light mb-1">Teşekkürler, siparişiniz başarıyla oluşturuldu.</p>
              {checkout.orderNo && (
                <div className="mt-4 mb-6 p-3 bg-cream rounded-xl">
                  <p className="text-xs text-coffee-light mb-0.5">Sipariş Numaranız</p>
                  <p className="text-lg font-bold text-coffee tracking-wider">{checkout.orderNo}</p>
                </div>
              )}
              <p className="text-sm text-coffee-light mb-8">Kargoya verildiğinde size bildirim göndereceğiz.</p>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-coffee text-white rounded-xl hover:bg-coffee-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Ana Sayfaya Dön
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Form Steps */}
              <div className="lg:col-span-2">
                {checkout.step === 'address' && (
                  <AddressForm
                    data={checkout.addressData}
                    errors={checkout.addressErrors}
                    onChange={checkout.setAddressData}
                    onNext={checkout.goToPayment}
                    savedAddresses={checkout.savedAddresses}
                    selectedAddressId={checkout.selectedAddressId}
                    onSelectAddress={checkout.selectSavedAddress}
                  />
                )}
                {checkout.step === 'payment' && (
                  <PaymentStep
                    paymentMethod={checkout.paymentMethod}
                    onChange={(m) => { checkout.setPaymentMethod(m); checkout.setPaymentError(''); }}
                    onBack={() => checkout.setStep('address')}
                    onNext={() => checkout.placeOrder(clearCart)}
                    loading={checkout.orderLoading}
                    error={checkout.orderError}
                    paymentError={checkout.paymentError}
                    settings={settings}
                    codLimitExceeded={checkout.codLimitExceeded}
                    codUpperLimit={checkout.codUpperLimit}
                  />
                )}
              </div>

              {/* Right: Order Summary */}
              <div>
                <OrderSummary
                  items={items}
                  totalPrice={totalPrice}
                  shippingCost={checkout.shippingCost}
                  codFee={checkout.codFee}
                  discount={checkout.discount}
                  grandTotal={checkout.grandTotal}
                  freeShipping={checkout.freeShipping}
                  couponCode={checkout.couponCode}
                  coupon={checkout.coupon}
                  couponError={checkout.couponError}
                  couponLoading={checkout.couponLoading}
                  onCouponChange={checkout.setCouponCode}
                  onApplyCoupon={checkout.applyCoupon}
                  onRemoveCoupon={checkout.removeCoupon}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
